import type { ReactNode } from "react";
import type { ReactElement } from "react";
import { Box, Divider, Typography, useTheme } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import type { TypographyProps } from "@mui/material/Typography";
import { roundPrice } from "@/utils/app-helper";
import { formatMoney as formatMoneyDefault } from "@/utils/locale-format";
import { AnimatedPrice } from "@/components/pricing/AnimatedPrice";
import { sanitizeNonNegativeMoney } from "@/components/pricing/sanitize-money";

const MONEY_EQ = 0.005;

export type PriceSummaryProps = {
  subtotal: unknown;
  discount: unknown;
  total: unknown;
  formatMoney?: (amount: number) => string;
  subtotalLabel?: string;
  totalLabel?: string;
  /** Shown under the total label (e.g. “Estimated”). */
  totalHint?: string;
  /** Optional rows between subtotal and divider (taxes, fees, etc.). */
  footerBeforeTotal?: ReactNode;
  /** Passed to {@link AnimatedPrice} for assistive text. */
  accessibilityTotalName?: string;
  /** Tighter spacing and typography for footers (e.g. checkout bar). */
  compact?: boolean;
  /**
   * When false, hide subtotal / fee rows and the ledger divider.
   * Order total (and hint) stay visible. Presentation-only — does not change amounts.
   */
  showDetailRows?: boolean;
  /**
   * When false, hide the total row. Used when checkout renders Order total as the
   * disclosure header. Presentation-only — does not change amounts.
   */
  showTotal?: boolean;
  /** Merged into {@link AnimatedPrice} `typographyProps` for the total amount. */
  animatedTotalTypographyProps?: TypographyProps;
  sx?: SxProps<Theme>;
  /** Merged into the ledger divider between adjustments and total (e.g. checkout bill card). */
  dividerSx?: SxProps<Theme>;
};

/**
 * Ledger-style totals: subtotal → optional charges → total. Reward copy lives in {@link RewardSummary}.
 */
export function PriceSummary({
  subtotal,
  discount,
  total,
  formatMoney = formatMoneyDefault,
  subtotalLabel = "Subtotal",
  totalLabel = "Total",
  totalHint,
  footerBeforeTotal,
  accessibilityTotalName,
  compact = false,
  showDetailRows = true,
  showTotal = true,
  animatedTotalTypographyProps,
  sx,
  dividerSx,
}: PriceSummaryProps): ReactElement {
  const theme = useTheme();
  const safeSubtotal = sanitizeNonNegativeMoney(subtotal);
  const safeDiscount = sanitizeNonNegativeMoney(discount);
  const safeTotal = sanitizeNonNegativeMoney(total);
  const a11yTotal = accessibilityTotalName ?? totalLabel;

  const expectedNet = roundPrice(safeSubtotal - safeDiscount);
  const showStrikeSubtotal =
    safeDiscount > MONEY_EQ &&
    safeSubtotal > MONEY_EQ &&
    Math.abs(roundPrice(safeTotal) - expectedNet) < MONEY_EQ;

  const gap = compact ? 0.75 : 1.25;
  const subFont = compact ? theme.typography.pxToRem(13) : theme.typography.pxToRem(14);
  const totalTitleFont = compact ? theme.typography.pxToRem(15) : theme.typography.pxToRem(16);
  const totalValueFont = compact ? theme.typography.pxToRem(17) : theme.typography.pxToRem(18);
  const valueWeight = compact ? theme.typography.fontWeightMedium : theme.typography.fontWeightMedium;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap, ...sx }}>
      {showDetailRows ? (
        <Box className="flexBetweenCenter" sx={{ alignItems: "flex-start", gap: 1 }}>
          <Typography
            component="p"
            sx={{
              m: 0,
              fontSize: subFont,
              fontWeight: theme.typography.fontWeightRegular,
              color: theme.palette.text.secondary,
            }}
          >
            {subtotalLabel}
          </Typography>
          <Typography
            component="p"
            sx={{
              m: 0,
              fontSize: subFont,
              fontWeight: valueWeight,
              color: theme.palette.text.primary,
              fontVariantNumeric: "tabular-nums",
              fontFeatureSettings: '"tnum" 1',
            }}
          >
            {formatMoney(safeSubtotal)}
          </Typography>
        </Box>
      ) : null}

      {showDetailRows && footerBeforeTotal ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: compact ? 0.75 : 1, mt: compact ? 0.25 : 0.5 }}>{footerBeforeTotal}</Box>
      ) : null}

      {showDetailRows && showTotal ? (
        <Divider
          sx={[
            { borderColor: theme.palette.divider },
            ...(dividerSx ? (Array.isArray(dividerSx) ? dividerSx : [dividerSx]) : []),
          ]}
        />
      ) : null}

      {showTotal ? (
      <Box className="flexBetweenCenter" sx={{ alignItems: "flex-end", gap: 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="p"
            sx={{
              m: 0,
              fontSize: totalTitleFont,
              fontWeight: theme.typography.fontWeightBold,
              color: theme.palette.text.primary,
              lineHeight: 1.2,
            }}
          >
            {totalLabel}
          </Typography>
          {totalHint ? (
            <Typography
              component="p"
              variant="caption"
              sx={{
                m: 0,
                mt: 0.25,
                display: "block",
                color: theme.palette.text.secondary,
                fontStyle: "italic",
              }}
            >
              {totalHint}
            </Typography>
          ) : null}
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 0.25,
            flexShrink: 0,
            minHeight: theme.spacing(4),
          }}
        >
          {showDetailRows && showStrikeSubtotal ? (
            <Typography
              component="span"
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                textDecoration: "line-through",
                fontVariantNumeric: "tabular-nums",
                fontFeatureSettings: '"tnum" 1',
              }}
              aria-hidden
            >
              {formatMoney(safeSubtotal)}
            </Typography>
          ) : null}
          <AnimatedPrice
            value={safeTotal}
            formatValue={formatMoney}
            accessibilityName={a11yTotal}
            typographyProps={{
              fontSize: totalValueFont,
              fontWeight: theme.typography.fontWeightBold,
              color: theme.palette.primary.main,
              lineHeight: 1.15,
              ...animatedTotalTypographyProps,
            }}
          />
        </Box>
      </Box>
      ) : null}
    </Box>
  );
}
